import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, X, UserPlus, Upload, Shield, Copy, Check, Gauge, Trash2, MoreVertical, Users as UsersIcon, BarChart3, Building2, KeyRound, CheckCircle, Coins, Pencil } from 'lucide-react';
import { getUsers, createUser, startBulkUpload, getBulkJobStatus, makeAdmin, removeAdmin, getTiers, setUserTier, deleteUser, resetUserRateLimits } from '../api';
import UserUsageModal from './UserUsageModal';
import {
  getOrg, updateOrgMember, removeOrgMember, searchOrgUsers, createOrgInvite,
  getOrgInvites, revokeOrgInvite, getJoinRequests, approveJoinRequest, rejectJoinRequest,
  transferOrgOwnership, setOrgCredits, resetOrgCredits,
  setMemberCredits, resetMemberCredits, bulkSetMemberCredits, getAllUserIds,
} from '../api';
import { getActiveOrg, isOrgManager } from '../org';

// Ownership moves by transfer, so it is never one of the assignable roles.
const ORG_ROLES = ['member', 'admin'];

const AVATAR_COLORS = [
  'bg-orange/15 text-orange',
  'bg-blue-500/15 text-blue-400',
  'bg-emerald-500/15 text-emerald-400',
  'bg-purple-500/15 text-purple-400',
  'bg-rose-500/15 text-rose-400',
  'bg-cyan-500/15 text-cyan-400',
  'bg-amber-500/15 text-amber-400',
];

function initials(name, email) {
  const src = (name || '').trim() || (email || '');
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

// Hide native number-input spinners for cleaner monetary fields.
const NO_SPIN = '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
const usd = (n) => '$' + Math.max(Number(n) || 0, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || '').length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState([]);
  const [filterTier, setFilterTier] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [menu, setMenu] = useState(null); // { user, x, y }
  const [usageUser, setUsageUser] = useState(null); // user to show in usage modal

  // Platform admin vs org owner/admin. Org admins reach this page too, but the
  // install-wide actions (grant platform admin, delete an account) are refused
  // by the API for them — so don't render the controls at all.
  const isPlatformAdmin = localStorage.getItem('admin_is_admin') === 'true';
  const orgId = getActiveOrg();
  // Org role decides, not `is_admin` — a platform admin who is only a member
  // here manages nothing here, same as the API now enforces.
  const canManageOrg = !!orgId && isOrgManager();
  const adminEmail = localStorage.getItem('admin_email');

  // Org membership state — this page absorbed the old Members tab, so that
  // everything about "who is in this org" lives in one place instead of being
  // split across two tabs by which collection the data happened to sit in.
  const [org, setOrg] = useState(null);
  const [invites, setInvites] = useState([]);
  const [requests, setRequests] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteMatches, setInviteMatches] = useState([]);
  const [copiedInvite, setCopiedInvite] = useState(null);

  // Depleting credit balances — distinct from the rolling-window budget on
  // Tiers. Org pool (this org's own owner/admin, or a platform admin) lives
  // here since this page absorbed the old Members tab. A member's sub-cap
  // WITHIN this org (org_members, not a global field — never leaks into
  // their standing in any other org) is set from the row menu below.
  const [editingOrgCredits, setEditingOrgCredits] = useState(false);
  const [orgCreditLimitInput, setOrgCreditLimitInput] = useState('');
  const [orgTopUpInput, setOrgTopUpInput] = useState('');
  const [creditUser, setCreditUser] = useState(null); // member to edit in the dialog
  const [memberCreditLimitInput, setMemberCreditLimitInput] = useState('');
  // Bulk sub-cap — set or clear the same limit across several members at
  // once, instead of one row at a time. `selectAllMatching` distinguishes
  // "every row on this page" from "every user matching the current search/
  // filters, across all pages" — the latter is resolved server-side (GET
  // /api/admin/users/ids) since a roster can run into the thousands.
  const [selectedForCredits, setSelectedForCredits] = useState(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [fetchingAllIds, setFetchingAllIds] = useState(false);
  const [bulkCreditInput, setBulkCreditInput] = useState('');
  const [bulkCreditBusy, setBulkCreditBusy] = useState(false);

  useEffect(() => { getTiers().then((r) => setTiers(r.data.tiers || [])).catch(() => {}); }, []);

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.user_email_id}? This cannot be undone.`)) return;
    try {
      await deleteUser(user.user_id);
      setMessage({ type: 'success', text: `${user.user_email_id} deleted` });
      loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete user' });
    }
  };

  const changeTier = async (user, tierId) => {
    try {
      await setUserTier(user.user_id, tierId);
      setMessage({ type: 'success', text: `${user.user_email_id} → tier updated` });
      loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to set tier' });
    }
  };

  const doResetOrgCredits = async () => {
    if (!window.confirm(`Refill ${org?.name || 'this organization'}'s credit pool back to its limit?`)) return;
    try { await resetOrgCredits(orgId); setMessage({ type: 'success', text: 'Org credits reset' }); loadOrgData(); }
    catch (err) { setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to reset credits' }); }
  };

  // One Save applies whatever's filled in — the new limit and/or a top-up.
  const saveOrgCredits = async () => {
    const patch = {};
    if (orgCreditLimitInput.trim() !== '') patch.credit_limit = parseFloat(orgCreditLimitInput);
    if (orgTopUpInput.trim() !== '') patch.top_up = parseFloat(orgTopUpInput);
    if (!Object.keys(patch).length) return;
    try {
      await setOrgCredits(orgId, patch);
      setMessage({ type: 'success', text: 'Org credits updated' });
      setOrgCreditLimitInput(''); setOrgTopUpInput(''); setEditingOrgCredits(false);
      loadOrgData();
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update credits' }); }
  };

  const saveMemberCredits = async () => {
    if (!creditUser) return;
    // Blank clears the sub-cap (member bound only by the org pool); a
    // number (re)seeds the balance at that limit — no top-up here, unlike
    // the org pool, since this is an allowance handed out, not a wallet.
    const value = memberCreditLimitInput.trim() === '' ? null : parseFloat(memberCreditLimitInput);
    try {
      await setMemberCredits(orgId, creditUser.user_id, value);
      setMessage({ type: 'success', text: `${creditUser.user_email_id} → credit limit updated` });
      setCreditUser(null); setMemberCreditLimitInput('');
      loadUsers();
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update credits' }); }
  };

  const doResetMemberCredits = async () => {
    if (!creditUser) return;
    if (!window.confirm(`Refill ${creditUser.user_email_id}'s credit limit back to its ceiling?`)) return;
    try {
      await resetMemberCredits(orgId, creditUser.user_id);
      setMessage({ type: 'success', text: 'Credits reset' });
      setCreditUser(null);
      loadUsers();
    } catch (err) { setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to reset credits' }); }
  };

  const toggleCreditSelection = (userId) => {
    setSelectAllMatching(false);
    setSelectedForCredits((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  // Every currently-visible row this actor is actually allowed to set a
  // sub-cap on — used only to decide whether to render the header checkbox
  // at all (nothing on this page to select).
  const creditManageableUsers = users.filter((u) => u.can_assign_tier);

  // The header checkbox selects EVERY user in this org matching the current
  // search/filters — not just the ≤50 loaded onto this page — since a
  // roster can run into the thousands. One click either fetches the full
  // matching id list (server-side, unpaginated) or clears the selection.
  const toggleSelectAllForCredits = async () => {
    if (selectAllMatching || selectedForCredits.size > 0) {
      setSelectedForCredits(new Set());
      setSelectAllMatching(false);
      return;
    }
    setFetchingAllIds(true);
    try {
      const res = await getAllUserIds(search, { tier_id: filterTier || undefined, is_admin: filterRole === '' ? undefined : filterRole === 'admin' });
      setSelectedForCredits(new Set(res.data.user_ids || []));
      setSelectAllMatching(true);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to select all users' });
    } finally { setFetchingAllIds(false); }
  };

  // Shared by "apply to N" and "clear for N" — `value` is a number to set the
  // same sub-cap on every selected member, or null to clear it on all of
  // them. One request regardless of selection size — the backend applies it
  // in a single write and reports how many rows it actually touched.
  const bulkApplyMemberCredits = async (value) => {
    const ids = Array.from(selectedForCredits);
    if (!ids.length) return;
    setBulkCreditBusy(true);
    try {
      const res = await bulkSetMemberCredits(orgId, ids, value);
      const { matched = 0, skipped = 0 } = res.data || {};
      setMessage(skipped
        ? { type: 'error', text: `Updated ${matched} of ${ids.length} — ${skipped} skipped (a role you can't act on)` }
        : { type: 'success', text: `Updated credit limit for ${matched} member${matched === 1 ? '' : 's'}` });
      setSelectedForCredits(new Set()); setSelectAllMatching(false); setBulkCreditInput('');
      loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Bulk update failed' });
    } finally { setBulkCreditBusy(false); }
  };

  // Shared modal state
  const [message, setMessage] = useState(null);

  // Add-user modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', is_admin: false, send_email: true });
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null); // { email, key, password, autoEmailEnabled }
  const [copiedField, setCopiedField] = useState(null); // 'key' | 'password' | null

  // Bulk upload modal state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState('upload'); // 'upload' | 'preview'
  const [bulkEmails, setBulkEmails] = useState([]);
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkParseError, setBulkParseError] = useState(null);
  const [bulkSendEmail, setBulkSendEmail] = useState(true);
  const [bulkCreating, setBulkCreating] = useState(false);

  // Floating progress widget state (persists across modal close)
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkWidgetMinimized, setBulkWidgetMinimized] = useState(false);

  const getBulkProgress = (job) => {
    if (!job || job.status === 'failed') return 0;
    if (job.status === 'done') return 100;
    if (job.phase === 'provisioning') {
      const total = job.total || 1;
      return Math.round((job.provisioned / total) * 35);
    }
    if (job.phase === 'sending') {
      const et = job.emails_total || 1;
      return Math.round(35 + (job.emails_sent / et) * 65);
    }
    return 0;
  };

  const getBulkPhaseLabel = (job) => {
    if (!job) return '';
    if (job.status === 'done') return `Done — ${job.created} created, ${job.skipped} skipped, ${job.failed} failed`;
    if (job.status === 'failed') return 'Job failed';
    if (job.phase === 'provisioning') return `Creating accounts… ${job.provisioned} / ${job.total}`;
    if (job.phase === 'sending') return `Sending emails… ${job.emails_sent} / ${job.emails_total}`;
    return 'Starting…';
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setMessage(null);
    try {
      const res = await createUser({
        email: newUser.email.trim(),
        is_admin: newUser.is_admin,
        send_email: newUser.send_email,
      });
      setShowAddModal(false);
      setNewUser({ email: '', is_admin: false, send_email: true });
      setRevealedKey({
        email: res.data.user_email_id,
        key: res.data.api_key,
        password: res.data.temp_password,
        autoEmailEnabled: res.data.auto_email_enabled,
      });
      if (!res.data.auto_email_enabled) {
        setMessage({ type: 'info', text: `User created — automatic emails are off (Platform Settings → General). Share the credentials below with ${res.data.user_email_id} yourself.` });
      } else if (newUser.send_email) {
        setMessage(res.data.email_sent
          ? { type: 'success', text: `User created — welcome email sent to ${res.data.user_email_id}.` }
          : { type: 'error', text: `User created, but the email failed: ${res.data.email_error || 'check SMTP config'}` });
      }
      loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to create user' });
    } finally {
      setCreating(false);
    }
  };

  const openBulkModal = () => {
    setBulkStep('upload');
    setBulkEmails([]);
    setBulkFileName('');
    setBulkParseError(null);
    setBulkSendEmail(true);
    setShowBulkModal(true);
  };

  const handleBulkFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBulkFileName(file.name);
    setBulkParseError(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        if (!rows.length) { setBulkParseError('File appears to be empty.'); return; }
        const headers = rows[0].map((h) => String(h || '').toLowerCase().trim());
        const colIdx = headers.findIndex((h) => h === 'email' || h === 'emails' || h === 'mail' || h === 'mails');
        if (colIdx === -1) { setBulkParseError('No column named "email", "emails", "mail", or "mails" found in the first row.'); return; }
        const emails = [...new Set(
          rows.slice(1)
            .map((row) => String(row[colIdx] || '').trim().toLowerCase())
            .filter((e) => e.includes('@') && e.includes('.'))
        )];
        if (!emails.length) { setBulkParseError('No valid email addresses found in the file.'); return; }
        setBulkEmails(emails);
        setBulkStep('preview');
      } catch (err) {
        setBulkParseError('Could not parse the file. Make sure it is a valid CSV or Excel file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleBulkCreate = async () => {
    setBulkCreating(true);
    try {
      const res = await startBulkUpload({ emails: bulkEmails, send_email: bulkSendEmail });
      setBulkJob({
        jobId: res.data.job_id,
        total: res.data.total,
        status: 'running',
        phase: 'provisioning',
        provisioned: 0,
        emails_total: 0,
        emails_sent: 0,
        created: 0,
        skipped: 0,
        failed: 0,
      });
      setBulkWidgetMinimized(false);
      setShowBulkModal(false);
    } catch (err) {
      setBulkParseError(err.response?.data?.detail || 'Bulk creation failed.');
    } finally {
      setBulkCreating(false);
    }
  };

  const handleToggleAdmin = async (user) => {
    const fn = user.is_admin ? removeAdmin : makeAdmin;
    if (!window.confirm(`${user.is_admin ? 'Remove platform admin from' : 'Grant platform admin to'} ${user.user_email_id}? This is install-wide and does not change their role in any organization.`)) return;
    try {
      await fn(user.user_id);
      setMessage({ type: 'success', text: `${user.user_email_id} → ${user.is_admin ? 'user' : 'admin'}` });
      loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed' });
    }
  };

  const handleResetRL = async (user) => {
    if (!window.confirm(`Reset rate limits for ${user.user_email_id}? Clears their consumed quota in this organization (personal + all teams here). Limits are unchanged.`)) return;
    try {
      const res = await resetUserRateLimits(user.user_id);
      setMessage({ type: 'success', text: `Rate limits reset — ${res.data.cleared} counter(s) cleared.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to reset rate limits' });
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, search, filterTier, filterRole]);
  useEffect(() => {
    setSelectedForCredits(new Set());
    setSelectAllMatching(false);
  }, [search, filterTier, filterRole]);

  // Poll bulk job progress every 2 s while a job is running
  useEffect(() => {
    if (!bulkJob?.jobId || bulkJob.status === 'done' || bulkJob.status === 'failed') return;
    const id = setInterval(async () => {
      try {
        const res = await getBulkJobStatus(bulkJob.jobId);
        setBulkJob((prev) => ({ ...prev, ...res.data }));
        if (res.data.status === 'done' || res.data.status === 'failed') {
          loadUsers();
        }
      } catch (_) {}
    }, 2000);
    return () => clearInterval(id);
  }, [bulkJob?.jobId, bulkJob?.status]);

  // close the row menu if the page scrolls or resizes (fixed menu would detach)
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [menu]);

  // Org membership data (invites / join requests / access policy). Managers
  // only, and only inside an org.
  const loadOrgData = async () => {
    if (!canManageOrg) { setOrg(null); setInvites([]); setRequests([]); return; }
    try {
      const [o, inv, req] = await Promise.all([
        getOrg(orgId), getOrgInvites(orgId), getJoinRequests(orgId),
      ]);
      setOrg(o.data);
      setInvites(inv.data.invites || []);
      setRequests(req.data.requests || []);
    } catch (_) { /* non-fatal — the user table still renders */ }
  };
  useEffect(() => { loadOrgData(); /* eslint-disable-next-line */ }, [orgId]);

  // Typeahead for the invite box (org members by name, plus an exact-email
  // probe for outsiders — see orgs.search_users).
  useEffect(() => {
    if (!canManageOrg || inviteEmail.trim().length < 2) { setInviteMatches([]); return; }
    let alive = true;
    const t = setTimeout(() => {
      searchOrgUsers(orgId, inviteEmail.trim())
        .then((r) => alive && setInviteMatches(r.data.users || []))
        .catch(() => {});
    }, 250);
    return () => { alive = false; clearTimeout(t); };
    /* eslint-disable-next-line */
  }, [inviteEmail, orgId]);

  const changeOrgRole = async (user, role) => {
    try {
      await updateOrgMember(orgId, user.user_id, role);
      setMessage({ type: 'success', text: `${user.user_email_id} → ${role}` });
      loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to change role' });
    }
  };

  const transferOwnership = async (user) => {
    if (!window.confirm(`Make ${user.user_email_id} the owner of ${org?.name || 'this organization'}? You'll become an admin.`)) return;
    try {
      await transferOrgOwnership(orgId, user.user_id);
      setMessage({ type: 'success', text: `${user.user_email_id} is now the owner` });
      window.location.reload();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Transfer failed' });
    }
  };

  const removeFromOrg = async (user) => {
    if (!window.confirm(`Remove ${user.user_email_id} from ${org?.name || 'this organization'}? Their account is not deleted.`)) return;
    try {
      await removeOrgMember(orgId, user.user_id);
      setMessage({ type: 'success', text: `${user.user_email_id} removed from the organization` });
      loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to remove' });
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      const r = await createOrgInvite(orgId, inviteEmail.trim(), inviteRole);
      navigator.clipboard?.writeText(window.location.origin + r.data.accept_path).catch(() => {});
      setMessage({ type: 'success', text: `Invite created for ${inviteEmail.trim()} — link copied` });
      setInviteEmail(''); setInviteMatches([]);
      loadOrgData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Invite failed' });
    }
  };

  const decideRequest = async (rid, approve) => {
    try {
      await (approve ? approveJoinRequest : rejectJoinRequest)(orgId, rid);
      setMessage({ type: 'success', text: approve ? 'Request approved' : 'Request declined' });
      await loadOrgData(); loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed' });
    }
  };

  const revokeInvite = async (mail) => {
    try { await revokeOrgInvite(orgId, mail); loadOrgData(); }
    catch (_) { setMessage({ type: 'error', text: 'Failed to revoke invite' }); }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const filters = {
        tier_id: filterTier || undefined,
        is_admin: filterRole === '' ? undefined : filterRole === 'admin',
      };
      const response = await getUsers(page, 50, search, filters);
      setUsers(response.data.users);
      setTotal(response.data.total);
      setPages(response.data.pages);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const openMenu = (e, user) => {
    const r = e.currentTarget.getBoundingClientRect();
    const MENU_H = 236; // approx menu height; flip up if it would overflow the viewport
    const openUp = r.bottom + MENU_H + 8 > window.innerHeight;
    const top = openUp ? Math.max(8, r.top - MENU_H - 6) : r.bottom + 6;
    setMenu({ user, x: r.right, y: top });
  };
  const runFromMenu = (fn) => { const u = menu?.user; setMenu(null); if (u) fn(u); };

  return (
    <div className="w-full">
      {/* header */}
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-fg flex items-center gap-2"><UsersIcon className="w-6 h-6 text-orange" />Users</h1>
          <p className="text-fg-muted text-sm mt-1">Provision accounts, assign tiers, manage API keys and access.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-fg-muted text-sm px-3 py-2 bg-field border border-field-border rounded-lg">{total.toLocaleString()} total</span>
          <button
            onClick={openBulkModal}
            className="flex items-center gap-2 px-3.5 py-2 bg-field border border-field-border hover:border-orange text-fg font-medium rounded-lg text-sm transition-colors"
          >
            <Upload className="w-4 h-4" /> Bulk upload
          </button>
          <button
            onClick={() => { setNewUser({ email: '', is_admin: false, send_email: true }); setMessage(null); setShowAddModal(true); }}
            className="flex items-center gap-2 px-3.5 py-2 bg-orange hover:bg-orange-hover text-white font-medium rounded-lg text-sm transition-colors"
          >
            <UserPlus className="w-4 h-4" /> Add user
          </button>
        </div>
      </div>

      {/* API key (+ temp password, when auto-email is off) reveal - shown once after create/regenerate */}
      {revealedKey && (
        <div className="mb-5 p-4 rounded-xl border border-orange/40 bg-orange/5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-fg text-sm font-medium flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-orange" />
              Credentials for {revealedKey.email} — copy them now, they won't be shown again
            </span>
            <button onClick={() => setRevealedKey(null)} className="text-fg-muted hover:text-fg"><X className="w-4 h-4" /></button>
          </div>
          {!revealedKey.autoEmailEnabled && (
            <p className="text-fg-muted text-xs mb-2">Automatic emails are off (Platform Settings → General) — send these to the user yourself.</p>
          )}
          {!revealedKey.autoEmailEnabled && (
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1">
                <div className="text-fg-muted text-[11px] mb-0.5">Email</div>
                <code className="block px-3 py-2 bg-field border border-field-border rounded-lg text-orange text-sm font-mono break-all">{revealedKey.email}</code>
              </div>
              <button onClick={() => { navigator.clipboard?.writeText(revealedKey.email); setCopiedField('email'); setTimeout(() => setCopiedField(null), 1500); }}
                className="self-end flex items-center gap-1.5 px-3 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm transition-colors">
                {copiedField === 'email' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{copiedField === 'email' ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}
          {revealedKey.key && (
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1">
                <div className="text-fg-muted text-[11px] mb-0.5">API key</div>
                <code className="block px-3 py-2 bg-field border border-field-border rounded-lg text-orange text-sm font-mono break-all">{revealedKey.key}</code>
              </div>
              <button onClick={() => { navigator.clipboard?.writeText(revealedKey.key); setCopiedField('key'); setTimeout(() => setCopiedField(null), 1500); }}
                className="self-end flex items-center gap-1.5 px-3 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm transition-colors">
                {copiedField === 'key' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{copiedField === 'key' ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}
          {revealedKey.password && !revealedKey.autoEmailEnabled && (
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <div className="text-fg-muted text-[11px] mb-0.5">Web UI password</div>
                <code className="block px-3 py-2 bg-field border border-field-border rounded-lg text-orange text-sm font-mono break-all">{revealedKey.password}</code>
              </div>
              <button onClick={() => { navigator.clipboard?.writeText(revealedKey.password); setCopiedField('password'); setTimeout(() => setCopiedField(null), 1500); }}
                className="self-end flex items-center gap-1.5 px-3 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm transition-colors">
                {copiedField === 'password' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{copiedField === 'password' ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* message */}
      {message && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm flex justify-between items-center ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : message.type === 'info' ? 'bg-orange/10 border border-orange/20 text-orange' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          <span>{message.text}</span><button onClick={() => setMessage(null)} className="hover:opacity-70"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── Organization membership (absorbed from the old Members tab) ──
          Join requests, invites and roles all answer "who is in this org and
          how do they get in", so they belong beside the people they affect
          rather than on a second tab. Managers only, inside an org only. */}
      {canManageOrg && requests.length > 0 && (
        <div className="mb-5 bg-panel border border-panel-border rounded-xl p-5">
          <div className="text-sm font-semibold text-fg mb-3">Join requests ({requests.length})</div>
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.request_id} className="flex items-center justify-between text-sm">
                <span className="text-fg">{r.name || r.email}<span className="text-fg-muted text-xs ml-2">{r.email}</span></span>
                <div className="flex items-center gap-2">
                  <button onClick={() => decideRequest(r.request_id, true)} className="flex items-center gap-1 px-2.5 py-1 bg-green-500/15 text-green-400 rounded text-xs hover:bg-green-500/25"><Check className="w-3.5 h-3.5" />Approve</button>
                  <button onClick={() => decideRequest(r.request_id, false)} className="flex items-center gap-1 px-2.5 py-1 text-red-400 rounded text-xs hover:bg-red-500/10"><X className="w-3.5 h-3.5" />Decline</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {canManageOrg && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5 items-start">
          <div className="bg-panel border border-panel-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <UserPlus className="w-4 h-4 text-orange" />
              <span className="text-sm font-semibold text-fg">Invite to {org?.name || 'this organization'}</span>
            </div>
            <div className="flex flex-wrap items-start gap-2">
              <div className="relative flex-1 min-w-[240px]">
                <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="email@company.com"
                  className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm focus:border-orange" />
                {inviteMatches.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-panel border border-panel-border rounded-lg shadow-lg max-h-56 overflow-auto">
                    {inviteMatches.map((u) => (
                      <button key={u.email} disabled={u.is_member} onClick={() => { setInviteEmail(u.email); setInviteMatches([]); }}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-fg/5 disabled:opacity-40">
                        <span className="truncate text-fg">
                          {u.name || u.email}
                          {u.name && <span className="text-fg-muted text-xs ml-1.5">{u.email}</span>}
                        </span>
                        <span className="text-[10px] text-fg-muted shrink-0">
                          {u.is_member ? 'already a member' : 'has an account'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                className="px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm focus:border-orange">
                {ORG_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button onClick={sendInvite} className="px-4 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm">Invite</button>
            </div>
            {invites.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {invites.map((i) => (
                  <div key={i.email} className="flex items-center justify-between text-sm">
                    <span className="text-fg-muted">{i.email} · {i.role}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { navigator.clipboard?.writeText(window.location.origin + i.accept_path); setCopiedInvite(i.email); setTimeout(() => setCopiedInvite(null), 1500); }}
                        className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg">
                        {copiedInvite === i.email ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />} link
                      </button>
                      <button onClick={() => revokeInvite(i.email)} className="text-xs text-red-400 hover:underline">revoke</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* org credit pool — depleting balance, distinct from a tier's
              rolling-window budget. Visible to owners/admins (canManageOrg) —
              it's their org's own balance, and this is the ONE place it's
              shown, rather than scattered across every usage page. Editing
              (top-up/reset) is this org's own owner/admin, or a platform
              admin — control over an org's own pool lives with the org,
              same as everything else on this page. */}
          <div className="bg-panel border border-panel-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Coins className="w-4 h-4 text-orange" />
                <span className="text-sm font-semibold text-fg">Credits — {org?.name || 'this organization'}</span>
                <div className="relative group">
                  <span className="text-fg-muted text-xs cursor-help">ⓘ</span>
                  <div className="pointer-events-none absolute left-0 top-full mt-1 w-64 rounded-lg bg-fg text-surface text-xs px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-75 z-10">
                    Live balance since the limit was last set/reset — different from the date-windowed totals on Dashboard/Usage Analytics.
                  </div>
                </div>
              </div>
              {!editingOrgCredits && (
                <button onClick={() => { setEditingOrgCredits(true); setOrgCreditLimitInput(''); setOrgTopUpInput(''); }}
                  className="flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors">
                  <Pencil className="w-3.5 h-3.5" />Edit
                </button>
              )}
            </div>

            {org?.credit_limit != null ? (() => {
              const remaining = Math.max(org.credits_remaining ?? 0, 0);
              const used = Math.max(org.credit_limit - remaining, 0);
              const usedPct = org.credit_limit > 0 ? Math.min(100, (used / org.credit_limit) * 100) : 0;
              const depleted = remaining <= 0;
              return (
                <>
                  <div className="flex items-center justify-between text-xs mb-2 gap-3">
                    <span className="text-fg-muted tabular-nums truncate">
                      <span className="text-fg font-medium">{usd(remaining)}</span> remaining of {usd(org.credit_limit)}
                    </span>
                    <span className={`tabular-nums shrink-0 ${depleted ? 'text-red-400' : 'text-fg-muted'}`}>{Math.round(usedPct)}% used</span>
                  </div>
                  <div className="h-2 rounded-full bg-field border border-field-border overflow-hidden mb-3">
                    <div className={`h-full ${depleted ? 'bg-red-500' : 'bg-orange'}`} style={{ width: `${usedPct}%` }} />
                  </div>
                </>
              );
            })() : (
              <p className="text-fg-muted text-sm mb-3">No credit limit configured — usage is unlimited.</p>
            )}

            {editingOrgCredits && (
              <div className="mt-4">
                <div className="flex flex-wrap gap-x-6 gap-y-4">
                  {/* Set limit */}
                  <div>
                    <label className="block text-sm font-medium text-fg">Set limit</label>
                    <p className="text-xs text-fg-muted mb-1.5">Applies to the entire organization.</p>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted text-sm pointer-events-none">$</span>
                      <input autoFocus value={orgCreditLimitInput} onChange={(e) => setOrgCreditLimitInput(e.target.value)}
                        placeholder={org?.credit_limit != null ? String(org.credit_limit) : '500'}
                        type="number" min="0" step="0.01"
                        onKeyDown={(e) => { if (e.key === 'Enter') saveOrgCredits(); }}
                        className={`w-32 pl-6 pr-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm tabular-nums focus:border-orange ${NO_SPIN}`} />
                    </div>
                  </div>

                  {/* Top up */}
                  <div>
                    <label className="block text-sm font-medium text-fg">Top up</label>
                    <p className="text-xs text-fg-muted mb-1.5">Adds to the organization's credit balance.</p>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted text-sm pointer-events-none">$</span>
                      <input value={orgTopUpInput} onChange={(e) => setOrgTopUpInput(e.target.value)} placeholder="100"
                        type="number" step="0.01"
                        onKeyDown={(e) => { if (e.key === 'Enter') saveOrgCredits(); }}
                        className={`w-32 pl-6 pr-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm tabular-nums focus:border-orange ${NO_SPIN}`} />
                    </div>
                  </div>
                </div>

                {/* Save + Reset + Cancel */}
                <div className="flex items-center justify-end gap-2 mt-4">
                  <button onClick={saveOrgCredits} className="px-4 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm transition-colors">Save</button>
                  {org?.credit_limit != null && (
                    <button onClick={doResetOrgCredits} className="px-3 py-2 bg-field border border-field-border text-fg rounded-lg text-sm hover:border-orange transition-colors">Reset to limit</button>
                  )}
                  <button onClick={() => setEditingOrgCredits(false)} className="px-3 py-2 bg-field border border-field-border text-fg-muted hover:text-fg hover:border-orange rounded-lg text-sm transition-colors">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* search + filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Search by email, name, or ID…"
            className="w-full pl-10 pr-4 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm placeholder-fg-muted/60"
          />
        </div>
        <select
          value={filterTier}
          onChange={(e) => { setPage(1); setFilterTier(e.target.value); }}
          className="px-3 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm"
        >
          <option value="">All tiers</option>
          {tiers.map((t) => <option key={t.tier_id} value={t.tier_id}>{t.name}</option>)}
        </select>
        {/* Filters `users.is_admin`, which is the install-wide flag — distinct
            from the org role shown in the Role column. Labelled so the two
            aren't mistaken for each other. */}
        <select
          value={filterRole}
          onChange={(e) => { setPage(1); setFilterRole(e.target.value); }}
          className="px-3 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm"
        >
          <option value="">All roles</option>
          <option value="admin">Platform admin</option>
          <option value="user">User</option>
        </select>
        {(filterTier || filterRole) && (
          <button
            onClick={() => { setFilterTier(''); setFilterRole(''); setPage(1); }}
            className="px-3 py-2.5 text-fg-muted hover:text-fg text-sm flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" />Clear filters
          </button>
        )}
      </div>

      {/* bulk credit limit — set or clear the same limit across several
          selected members at once, instead of one row at a time. Org-scoped
          (same as the per-row "Set credit limit" action), so it's only
          offered with an org actually selected. */}
      {orgId && selectedForCredits.size > 0 && (() => {
        return (
          <div className="mb-3 bg-orange/10 border border-orange/30 rounded-xl px-4 py-3">
            {/* heading */}
            <div className="flex items-center justify-between gap-2 mb-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <Coins className="w-4 h-4 text-orange shrink-0" />
                <span className="text-sm font-semibold text-fg truncate">Set limits for individual users</span>
                <span className="text-sm text-fg-muted tabular-nums shrink-0">
                  ({selectAllMatching ? `All ${selectedForCredits.size}` : selectedForCredits.size})
                </span>
              </div>
              <button onClick={() => { setSelectedForCredits(new Set()); setSelectAllMatching(false); }} title="Clear selection"
                className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-field border border-field-border text-fg-muted hover:text-fg hover:border-orange transition-colors shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* controls */}
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-28">
                <p className="text-sm font-medium text-fg mb-1.5">Limit</p>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted text-sm pointer-events-none">$</span>
                  <input value={bulkCreditInput} onChange={(e) => setBulkCreditInput(e.target.value)} placeholder="20"
                    type="number" min="0" step="0.01" disabled={bulkCreditBusy}
                    className={`w-full pl-6 pr-2.5 py-1.5 bg-field border border-field-border rounded-lg text-fg text-sm tabular-nums focus:border-orange ${NO_SPIN}`} />
                </div>
              </div>
              <button
                disabled={bulkCreditBusy || bulkCreditInput.trim() === ''}
                onClick={() => bulkApplyMemberCredits(parseFloat(bulkCreditInput))}
                className="px-3 py-1.5 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm disabled:opacity-50">
                Apply
              </button>
              <button
                disabled={bulkCreditBusy}
                onClick={() => bulkApplyMemberCredits(null)}
                className="px-3 py-1.5 bg-field border border-field-border text-fg rounded-lg text-sm hover:border-orange disabled:opacity-50">
                Clear
              </button>
            </div>
          </div>
        );
      })()}

      {/* table */}
      <div className="bg-panel border border-panel-border rounded-2xl overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="text-left text-xs text-fg-muted bg-field/40 border-b border-panel-border">
              {orgId && creditManageableUsers.length > 0 && (
                <th className="pl-5 pr-2 py-3 w-8">
                  <input type="checkbox" className="accent-orange w-3.5 h-3.5" disabled={fetchingAllIds}
                    checked={selectAllMatching}
                    onChange={toggleSelectAllForCredits}
                    title={fetchingAllIds ? 'Selecting everyone…' : 'Select every user in this org (for bulk credit limit actions)'} />
                </th>
              )}
              <th className="px-5 py-3 font-medium">User</th>
              <th className="px-3 py-3 font-medium">Role</th>
              <th className="px-3 py-3 font-medium">Tier</th>
              <th className="px-3 py-3 font-medium">Registered</th>
              <th className="px-3 py-3 font-medium">Status</th>
              <th className="px-5 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const columnCount = 6 + (orgId && creditManageableUsers.length > 0 ? 1 : 0);
              if (loading) return <tr><td colSpan={columnCount} className="px-6 py-12 text-center text-fg-muted">Loading…</td></tr>;
              if (users.length === 0) return <tr><td colSpan={columnCount} className="px-6 py-12 text-center text-fg-muted">No users found</td></tr>;
              return null;
            })()}
            {!loading && users.length > 0 && (
              users.map((user) => (
                <tr key={user.user_id} className="border-b border-panel-border/50 last:border-0 hover:bg-field/30 transition-colors">
                  {orgId && creditManageableUsers.length > 0 && (
                    <td className="pl-5 pr-2 py-3">
                      {user.can_assign_tier && (
                        <input type="checkbox" className="accent-orange w-3.5 h-3.5"
                          checked={selectedForCredits.has(user.user_id)}
                          onChange={() => toggleCreditSelection(user.user_id)} />
                      )}
                    </td>
                  )}
                  {/* user */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3 max-w-[280px]">
                      <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${avatarColor(user.user_email_id)}`}>
                        {initials(user.user_name, user.user_email_id)}
                      </span>
                      <div className="min-w-0">
                        <div className="text-fg truncate" title={user.user_email_id}>{user.user_email_id}</div>
                        <div className="text-fg-muted text-xs truncate" title={user.user_name || ''}>{user.user_name || 'No name'}</div>
                      </div>
                    </div>
                  </td>
                  {/* role — org role inside an org, platform role outside.
                      They are different axes: org role (owner/admin/member)
                      governs this tenant; `is_admin` is install-wide. Showing
                      the platform one inside an org invited exactly the
                      confusion this page is meant to remove. */}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      {user.can_update_role ? (
                        <select
                          value={user.org_role || 'member'}
                          onChange={(e) => changeOrgRole(user, e.target.value)}
                          className="px-2 py-1 bg-field border border-field-border rounded-lg text-fg text-xs"
                        >
                          {ORG_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-field text-fg-muted">{user.org_role || 'member'}</span>
                      )}
                      {/* Platform admins still worth flagging — but as a badge
                          beside the org role, never instead of it. */}
                      {user.is_admin && isPlatformAdmin && (
                        <span title="Platform administrator" className="text-orange"><Shield className="w-3.5 h-3.5" /></span>
                      )}
                    </div>
                  </td>
                  {/* tier */}
                  <td className="px-3 py-3">
                    {(() => {
                      const inOrgList = tiers.some((t) => t.tier_id === user.tier_id);
                      // A tier missing from THIS org's list isn't necessarily
                      // deleted — it can be an inherited platform tier (public
                      // signups start on one). Only the backend failing to
                      // resolve a name means the tier row is really gone.
                      const missing = user.tier_id && !inOrgList && !user.tier_name;
                      return (
                        <select
                          value={missing ? '__unknown__' : (user.tier_id || '')}
                          disabled={!user.can_assign_tier}
                          onChange={(e) => changeTier(user, e.target.value)}
                          className={`px-2 py-1 bg-field border rounded-lg text-fg text-xs ${missing ? 'border-red-500 text-red-500' : 'border-field-border'}`}
                        >
                          <option value="" disabled>No tier</option>
                          {missing && (
                            <option value="__unknown__" disabled>Unknown tier (deleted) — reassign</option>
                          )}
                          {/* Inherited tier: render it so the select shows the
                              real name instead of falling back to a blank. */}
                          {user.tier_id && !inOrgList && user.tier_name && (
                            <option value={user.tier_id}>
                              {user.tier_name}{user.tier_inherited ? ' (inherited)' : ''}
                            </option>
                          )}
                          {tiers.map((t) => (<option key={t.tier_id} value={t.tier_id}>{t.name}</option>))}
                        </select>
                      );
                    })()}
                  </td>
                  {/* registered */}
                  <td className="px-3 py-3 text-fg-muted text-xs whitespace-nowrap">
                    {user.user_registration_date ? new Date(user.user_registration_date).toLocaleDateString() : '—'}
                  </td>
                  {/* status */}
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs text-green-400"><span className="w-1.5 h-1.5 rounded-full bg-green-400" />Active</span>
                  </td>
                  {/* actions */}
                  <td className="px-5 py-3 text-right">
                    <button onClick={(e) => openMenu(e, user)} title="Actions"
                      className="p-1.5 text-fg-muted hover:text-fg rounded-lg hover:bg-field transition-colors">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* row action menu (fixed, escapes table clipping) */}
      {menu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenu(null)} />
          <div className="fixed z-50 w-56 bg-panel border border-panel-border rounded-xl shadow-2xl py-1.5 overflow-y-auto"
            style={{ top: menu.y, left: Math.max(8, menu.x - 224), maxHeight: 'calc(100vh - 16px)' }}>
            <button onClick={() => { setUsageUser(menu.user); setMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-fg hover:bg-field text-left transition-colors">
              <BarChart3 className="w-4 h-4 text-fg-muted" />View usage
            </button>
            {/* This member's credit sub-cap WITHIN this org's pool — org_members,
                not a global field, so it can never leak into their standing
                in any other org. Same trust boundary as tier assignment
                (can_assign_tier): a platform admin, or that org's owner/admin
                acting on one of their own members. Org-scoped, so it only
                makes sense with an org actually selected. */}
            {orgId && menu.user.can_assign_tier && (
              <button onClick={() => {
                setCreditUser(menu.user);
                setMemberCreditLimitInput(menu.user.member_credit_limit != null ? String(menu.user.member_credit_limit) : '');
                setMenu(null);
              }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-fg hover:bg-field text-left transition-colors">
                <Coins className="w-4 h-4 text-fg-muted" />Set credit limit
              </button>
            )}

            {(() => {
              const u = menu.user;
              // One rule for the whole menu: inside an org you may only act on
              // someone below your own role, so nothing here is offered for the
              // owner or for a fellow admin — including the install-wide actions.
              const managePlatform = isPlatformAdmin && u.can_manage_account;
              const canDelete = managePlatform && !u.owns_org;
              const hasActions = u.can_manage_account || u.can_transfer_ownership || u.can_remove || managePlatform;

              if (!hasActions) {
                return (
                  <>
                    <div className="my-1.5 border-t border-panel-border" />
                    <p className="px-3 py-2 text-xs text-fg-muted leading-relaxed">
                      {u.org_role === 'owner'
                        ? 'Organization owner. Ownership moves by transfer, so there is nothing to change here.'
                        : 'You can only manage members below your own role.'}
                    </p>
                  </>
                );
              }

              return (
                <>
                  <div className="my-1.5 border-t border-panel-border" />
                  {managePlatform && (
                    <button onClick={() => runFromMenu(handleToggleAdmin)} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-fg hover:bg-field text-left transition-colors">
                      <Shield className="w-4 h-4 text-fg-muted" />{u.is_admin ? 'Remove platform admin' : 'Make platform admin'}
                    </button>
                  )}
                  {u.can_manage_account && (
                    <>
                      <button onClick={() => runFromMenu(handleResetRL)} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-fg hover:bg-field text-left transition-colors">
                        <Gauge className="w-4 h-4 text-fg-muted" />Reset rate limits
                      </button>
                    </>
                  )}
                  {u.can_transfer_ownership && (
                    <button onClick={() => runFromMenu(transferOwnership)} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-fg hover:bg-field text-left transition-colors">
                      <Building2 className="w-4 h-4 text-fg-muted" />Make organization owner
                    </button>
                  )}

                  <div className="my-1.5 border-t border-panel-border" />
                  {u.can_remove && (
                    <button onClick={() => runFromMenu(removeFromOrg)} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-400 hover:bg-field text-left transition-colors">
                      <X className="w-4 h-4" />Remove from organization
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => runFromMenu(handleDeleteUser)} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-400 hover:bg-field text-left transition-colors">
                      <Trash2 className="w-4 h-4" />Delete user
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </>
      )}

      {/* pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
            className="p-2 rounded-lg bg-field border border-field-border text-fg disabled:opacity-50 disabled:cursor-not-allowed hover:border-orange transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-fg-muted text-sm">Page {page} of {pages}</span>
          <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}
            className="p-2 rounded-lg bg-field border border-field-border text-fg disabled:opacity-50 disabled:cursor-not-allowed hover:border-orange transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-panel border border-panel-border rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-semibold text-fg flex items-center gap-2"><UserPlus className="w-5 h-5 text-orange" />Add user</h2>
              <button onClick={() => setShowAddModal(false)} className="text-fg-muted hover:text-fg p-1 rounded-lg hover:bg-field"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-fg-muted mb-1.5">Email <span className="text-orange">*</span></label>
                <input type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm" placeholder="user@org.com" />
              </div>
              {isPlatformAdmin && (
                <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
                  <input type="checkbox" checked={newUser.is_admin} onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })} className="accent-orange w-4 h-4" />
                  Make this user a platform admin
                </label>
              )}
              <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
                <input type="checkbox" checked={newUser.send_email} onChange={(e) => setNewUser({ ...newUser, send_email: e.target.checked })} className="accent-orange w-4 h-4" />
                Email the user their API key
              </label>
              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={creating}
                  className="flex-1 py-2.5 bg-orange hover:bg-orange-hover text-white font-medium rounded-lg transition-colors disabled:opacity-50">
                  {creating ? 'Creating…' : 'Create user'}
                </button>
                <button type="button" onClick={() => setShowAddModal(false)}
                  className="px-6 py-2.5 bg-field border border-field-border text-fg rounded-lg hover:border-orange transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-panel border border-panel-border rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-semibold text-fg flex items-center gap-2">
                <Upload className="w-5 h-5 text-orange" />
                {bulkStep === 'result' ? 'Bulk upload — results' : 'Bulk upload users'}
              </h2>
              <button onClick={() => setShowBulkModal(false)} className="text-fg-muted hover:text-fg p-1 rounded-lg hover:bg-field"><X className="w-5 h-5" /></button>
            </div>

            {/* Step: upload */}
            {bulkStep === 'upload' && (
              <div className="space-y-4">
                <p className="text-fg-muted text-sm">Upload a CSV or Excel file with an <code className="bg-field px-1 rounded text-fg text-xs">email</code> column. A random password will be generated and emailed to each user.</p>
                <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-field-border rounded-xl p-8 cursor-pointer hover:border-orange transition-colors">
                  <Upload className="w-8 h-8 text-fg-muted" />
                  <span className="text-fg text-sm font-medium">Click to choose a file</span>
                  <span className="text-fg-muted text-xs">.csv, .xlsx, .xls</span>
                  <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleBulkFile} />
                </label>
                {bulkParseError && <p className="text-red-400 text-sm">{bulkParseError}</p>}
                <button type="button" onClick={() => setShowBulkModal(false)}
                  className="w-full py-2.5 bg-field border border-field-border text-fg rounded-lg hover:border-orange transition-colors text-sm">Cancel</button>
              </div>
            )}

            {/* Step: preview */}
            {bulkStep === 'preview' && (
              <div className="space-y-4">
                <div className="p-3 bg-field rounded-lg flex items-center gap-2 text-sm text-fg-muted">
                  <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                  <span>Found <span className="text-fg font-medium">{bulkEmails.length}</span> unique email{bulkEmails.length !== 1 ? 's' : ''} in <span className="text-fg font-medium">{bulkFileName}</span></span>
                </div>
                <ul className="max-h-48 overflow-y-auto bg-field rounded-lg divide-y divide-panel-border text-sm">
                  {bulkEmails.slice(0, 20).map((e) => (
                    <li key={e} className="px-3 py-1.5 text-fg truncate">{e}</li>
                  ))}
                  {bulkEmails.length > 20 && (
                    <li className="px-3 py-1.5 text-fg-muted italic">…and {bulkEmails.length - 20} more</li>
                  )}
                </ul>
                <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
                  <input type="checkbox" checked={bulkSendEmail} onChange={(e) => setBulkSendEmail(e.target.checked)} className="accent-orange w-4 h-4" />
                  Email each user their temporary password and API key
                </label>
                {bulkParseError && <p className="text-red-400 text-sm">{bulkParseError}</p>}
                <div className="flex gap-3 pt-1">
                  <button onClick={handleBulkCreate} disabled={bulkCreating}
                    className="flex-1 py-2.5 bg-orange hover:bg-orange-hover text-white font-medium rounded-lg transition-colors disabled:opacity-50 text-sm">
                    {bulkCreating ? 'Creating accounts…' : `Create ${bulkEmails.length} account${bulkEmails.length !== 1 ? 's' : ''}`}
                  </button>
                  <button type="button" onClick={() => setBulkStep('upload')}
                    className="px-5 py-2.5 bg-field border border-field-border text-fg rounded-lg hover:border-orange transition-colors text-sm">Back</button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Floating bulk-upload progress widget */}
      {bulkJob && (
        <div className="fixed bottom-5 right-5 z-50">
          {bulkWidgetMinimized ? (
            /* Minimised chip */
            <button
              onClick={() => setBulkWidgetMinimized(false)}
              className="flex items-center gap-2 px-4 py-2 bg-panel border border-panel-border rounded-full shadow-2xl text-sm text-fg hover:border-orange transition-colors"
            >
              <Upload className="w-4 h-4 text-orange shrink-0" />
              <span>Bulk upload</span>
              <span className="text-fg-muted">·</span>
              <span className="font-medium text-orange">{getBulkProgress(bulkJob)}%</span>
              {bulkJob.status === 'done' && <CheckCircle className="w-4 h-4 text-green-400" />}
            </button>
          ) : (
            /* Full widget */
            <div className="w-80 bg-panel border border-panel-border rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-panel-border">
                <span className="text-fg font-medium text-sm flex items-center gap-2">
                  <Upload className="w-4 h-4 text-orange" />
                  Bulk upload
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setBulkWidgetMinimized(true)}
                    className="w-7 h-7 flex items-center justify-center text-fg-muted hover:text-fg hover:bg-field rounded-lg transition-colors text-lg leading-none">
                    −
                  </button>
                  {(bulkJob.status === 'done' || bulkJob.status === 'failed') && (
                    <button onClick={() => setBulkJob(null)}
                      className="w-7 h-7 flex items-center justify-center text-fg-muted hover:text-fg hover:bg-field rounded-lg transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="px-4 py-3 space-y-3">
                {/* Progress bar */}
                <div>
                  <div className="h-1.5 bg-field border border-field-border rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${getBulkProgress(bulkJob)}%` }}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-1.5">
                    <span className="text-xs text-fg-muted">{getBulkPhaseLabel(bulkJob)}</span>
                    <span className="text-xs font-medium text-orange">{getBulkProgress(bulkJob)}%</span>
                  </div>
                </div>

                {/* Live counters */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-field rounded-lg py-2">
                    <p className="text-base font-semibold text-green-400">{bulkJob.created ?? 0}</p>
                    <p className="text-xs text-fg-muted">Created</p>
                  </div>
                  <div className="bg-field rounded-lg py-2">
                    <p className="text-base font-semibold text-fg-muted">{bulkJob.skipped ?? 0}</p>
                    <p className="text-xs text-fg-muted">Skipped</p>
                  </div>
                  <div className="bg-field rounded-lg py-2">
                    <p className={`text-base font-semibold ${(bulkJob.failed ?? 0) > 0 ? 'text-red-400' : 'text-fg-muted'}`}>{bulkJob.failed ?? 0}</p>
                    <p className="text-xs text-fg-muted">Failed</p>
                  </div>
                </div>

                {/* Failed / skipped detail list (shown only when done) */}
                {bulkJob.status === 'done' && bulkJob.details?.filter(d => d.status !== 'created').length > 0 && (
                  <ul className="max-h-36 overflow-y-auto bg-field rounded-lg divide-y divide-panel-border text-xs">
                    {bulkJob.details.filter(d => d.status !== 'created').map((d) => (
                      <li key={d.email} className="px-3 py-1.5 flex items-center gap-2">
                        <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${d.status === 'skipped' ? 'bg-fg-muted' : 'bg-red-400'}`} />
                        <span className="text-fg truncate flex-1">{d.email}</span>
                        <span className="text-fg-muted shrink-0">{d.reason || d.status}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {bulkJob.status === 'failed' && (
                  <p className="text-red-400 text-xs">Job failed unexpectedly. Check server logs.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {/* User Usage Modal */}
      {usageUser && (
        <UserUsageModal
          user={usageUser}
          onClose={() => setUsageUser(null)}
        />
      )}

      {/* This member's credit sub-cap WITHIN this org's pool — org_members,
          never a global field, so it can't leak into their standing in any
          other org. No top-up here (unlike the org pool): setting a limit
          always re-seeds the balance, since this is an allowance handed
          out, not a wallet that accumulates. */}
      {creditUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-panel border border-panel-border rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-semibold text-fg flex items-center gap-2"><Coins className="w-5 h-5 text-orange" />Credit limit</h2>
              <button onClick={() => setCreditUser(null)} className="text-fg-muted hover:text-fg p-1 rounded-lg hover:bg-field"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-fg-muted text-sm mb-4">
              {creditUser.user_email_id}'s credit limit within {org?.name || 'this organization'} — on top of whatever the org-wide pool also allows.
            </p>
            {creditUser.member_credit_limit != null ? (() => {
              const remaining = Math.max(creditUser.member_credits_remaining ?? 0, 0);
              const used = Math.max(creditUser.member_credit_limit - remaining, 0);
              const usedPct = creditUser.member_credit_limit > 0 ? Math.min(100, (used / creditUser.member_credit_limit) * 100) : 0;
              const depleted = remaining <= 0;
              return (
                <>
                  <div className="mb-2 text-xs text-fg-muted tabular-nums relative group inline-flex items-center gap-1 w-full">
                    <span>Used <span className={`font-medium ${depleted ? 'text-red-400' : 'text-fg'}`}>{usd(used)}</span> of {usd(creditUser.member_credit_limit)}</span>
                    <span className="cursor-help">ⓘ</span>
                    <div className="pointer-events-none absolute left-0 top-full mt-1 w-64 rounded-lg bg-fg text-surface text-xs px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-75 z-10">
                      Since this limit was last set/reset — different from the date-windowed total on Usage Analytics.
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-field border border-field-border overflow-hidden mb-4">
                    <div className={`h-full ${depleted ? 'bg-red-500' : 'bg-orange'}`} style={{ width: `${usedPct}%` }} />
                  </div>
                </>
              );
            })() : (
              <p className="text-fg-muted text-sm mb-4">No credit limit configured — bound only by the org's pool.</p>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-fg-muted mb-1.5">Set limit ($) — blank clears it</label>
                <input value={memberCreditLimitInput} onChange={(e) => setMemberCreditLimitInput(e.target.value)}
                  placeholder={creditUser.member_credit_limit != null ? String(creditUser.member_credit_limit) : 'e.g. 50'}
                  type="number" min="0" step="0.01" className="w-full px-3 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm" />
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={saveMemberCredits}
                  className="flex-1 py-2.5 bg-orange hover:bg-orange-hover text-white font-medium rounded-lg transition-colors">Save</button>
                {creditUser.member_credit_limit != null && (
                  <button onClick={doResetMemberCredits}
                    className="px-4 py-2.5 bg-field border border-field-border text-fg rounded-lg hover:border-orange transition-colors">Reset to limit</button>
                )}
                <button onClick={() => setCreditUser(null)}
                  className="px-4 py-2.5 bg-field border border-field-border text-fg rounded-lg hover:border-orange transition-colors">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
